import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { Checkbox } from '../../../components/ui/checkbox';
import { ChevronDown, Lock, GraduationCap, X, ShieldCheck } from 'lucide-react';

// Menu déroulant multi-sélection des modules d'une journée, avec durée (h) par module.
export const ModulesJourPicker = ({ date, mods, formationModulesList, otherModulesList, modulesAft, lockedDay, modulesEvalues = new Set(), onChange }) => {
  const [open, setOpen] = useState(false);
  const selected = Object.fromEntries(mods.filter(mp => mp.module_id).map(mp => [mp.module_id, mp]));
  const total = mods.reduce((s, mp) => s + (parseFloat(mp.duree_heures) || 0), 0);
  const allModules = [...formationModulesList, ...otherModulesList];

  const toggle = (moduleId) => {
    if (selected[moduleId] && modulesEvalues.has(moduleId)) return; // module deja evalue : non supprimable
    if (selected[moduleId]) onChange(mods.filter(mp => mp.module_id !== moduleId));
    else onChange([...mods, { module_id: moduleId, duree_heures: Math.max(0.5, Math.min(7, 7 - total)), obligatoire: false }]);
  };
  const setDuree = (moduleId, v) => onChange(mods.map(mp => mp.module_id === moduleId ? { ...mp, duree_heures: v === '' ? 0 : v } : mp));

  const renderGroup = (label, list) => list.length > 0 && (
    <div className="space-y-0.5">
      <p className="px-2 pt-1.5 pb-0.5 text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
      {list.map(m => {
        const mp = selected[m.id];
        const aft = modulesAft?.[m.id];
        const evalue = !!mp && modulesEvalues.has(m.id);
        return (
          <div key={m.id} className={`flex items-center gap-2 px-2 py-1 rounded ${mp ? 'bg-violet-50' : 'hover:bg-slate-50'}`} data-testid={`module-option-${date}-${m.id}`}>
            <Checkbox checked={!!mp} disabled={lockedDay || evalue} onCheckedChange={() => toggle(m.id)} data-testid={`module-check-${date}-${m.id}`} title={evalue ? 'Module évalué : non supprimable' : ''} />
            <div className="min-w-0 flex-1 text-xs">
              <span className="font-mono text-violet-600 mr-1">{m.code}</span>
              <span className="text-slate-700 truncate">{m.titre}</span>
            </div>
            {evalue && <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded" title="Une évaluation a été réalisée sur ce module : il ne peut pas être retiré" data-testid={`module-evalue-${date}-${m.id}`}><ShieldCheck className="w-2.5 h-2.5" /> évalué</span>}
            {aft && (
              <Link to={`/dossiers/${aft.aft_id}/edit`} className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-cyan-700 bg-cyan-100 px-1.5 py-0.5 rounded" title="Module certifiant : les dates de l'AFT liée se synchronisent automatiquement" data-testid={`module-aft-lock-${date}-${m.id}`}>
                <Lock className="w-2.5 h-2.5" /> {aft.aft_code}
              </Link>
            )}
            {mp && (
              <div className="flex items-center gap-1 shrink-0">
                <input type="number" min="0.5" max="7" step="0.5" value={mp.duree_heures} disabled={lockedDay} onChange={(e) => setDuree(m.id, e.target.value)}
                  className="w-14 h-6 text-xs text-center border rounded disabled:bg-slate-100" data-testid={`module-duree-${date}-${m.id}`} />
                <span className="text-[10px] text-slate-400">h</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" disabled={lockedDay} className={`w-full min-h-[30px] flex items-center gap-1.5 flex-wrap px-2 py-1 text-left border rounded-md text-xs transition-colors ${lockedDay ? 'bg-slate-50 cursor-not-allowed' : 'bg-white hover:border-violet-300'}`} data-testid={`modules-picker-${date}`}>
          {mods.length === 0 && <span className="text-slate-400">Sélectionner les modules…</span>}
          {mods.map(mp => {
            const m = allModules.find(x => x.id === mp.module_id);
            const aft = modulesAft?.[mp.module_id];
            return (
              <span key={mp.module_id} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[10px] ${aft ? 'bg-cyan-100 text-cyan-800' : 'bg-violet-100 text-violet-700'}`} title={m?.titre}>
                {aft && <GraduationCap className="w-2.5 h-2.5" />}{m?.code || '?'} · {mp.duree_heures}h
                {!lockedDay && !modulesEvalues.has(mp.module_id) && <X className="w-2.5 h-2.5 ml-0.5 hover:text-red-600" onClick={(e) => { e.stopPropagation(); toggle(mp.module_id); }} data-testid={`module-remove-${date}-${mp.module_id}`} />}
                {!lockedDay && modulesEvalues.has(mp.module_id) && <ShieldCheck className="w-2.5 h-2.5 ml-0.5 text-emerald-600" title="Module évalué : non supprimable" />}
              </span>
            );
          })}
          <span className="ml-auto flex items-center gap-1 shrink-0">
            {mods.length > 0 && <span className={`text-[10px] font-semibold ${Math.abs(total - 7) < 0.01 ? 'text-emerald-600' : 'text-amber-600'}`} data-testid={`modules-total-${date}`}>{total}h</span>}
            {lockedDay ? <Lock className="w-3 h-3 text-slate-400" /> : <ChevronDown className="w-3 h-3 text-slate-400" />}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[420px] p-1.5 max-h-[360px] overflow-y-auto" data-testid={`modules-picker-content-${date}`}>
        {renderGroup('Modules de la formation', formationModulesList)}
        {renderGroup('Autres modules', otherModulesList)}
        <div className={`mt-1.5 px-2 py-1 border-t text-[11px] flex justify-between ${Math.abs(total - 7) < 0.01 ? 'text-emerald-600' : 'text-amber-600'}`}>
          <span>Total de la journée</span><span className="font-semibold">{total}h / 7h</span>
        </div>
      </PopoverContent>
    </Popover>
  );
};
